import type { Node } from "prosemirror-model";
import type { NodeView, NodeViewConstructor } from "prosemirror-view";
import { getOwner, runWithOwner, type VoidComponent } from "solid-js";
import { render } from "solid-js/web";
import { UnicodeEmoji } from "@/atoms/UnicodeEmoji";
import { getEmojiHex } from "@/lib/emoji";
import { getEmojiUrl } from "@/media/util.tsx";

export const createNodeViews = () => {
	const owner = getOwner();

	return <T extends Record<string, unknown>>(
		propsFn: (node: Node) => T,
		Component: VoidComponent<T>,
	) =>
		(node: Node): NodeView => {
			const dom = document.createElement("span");
			dom.classList.add("node-view-wrapper");

			// Capture props synchronously before rendering to avoid
			// reactive tracking inside the render callback
			const props = propsFn(node);
			let currentProps = props;

			const dispose = render(
				() => runWithOwner(owner, () => <Component {...currentProps} />),
				dom,
			);

			return {
				dom,
				update: (newNode: Node) => {
					currentProps = propsFn(newNode);
					return true;
					// Note: We don't re-render here to avoid creating new computations.
					// The component will re-render when its parent re-renders.
					// TODO: verify above
				},
				destroy: () => dispose(),
			};
		};
};

export const createEditorNodeViews = () => {
	const nv = createNodeViews();

	return (): Record<string, NodeViewConstructor> => ({
		mention: nv(
			(n) => ({
				id: n.attrs.user,
				name: n.attrs.name ?? n.attrs.user ?? "...",
			}),
			(props) => {
				return <span class="mention mention-user">@{props.name}</span>;
			},
		),
		mentionChannel: nv(
			(n) => ({
				id: n.attrs.channel,
				name: n.attrs.name ?? n.attrs.channel ?? "...",
			}),
			(props) => {
				return <span class="mention mention-channel">#{props.name}</span>;
			},
		),
		mentionRole: nv(
			(n) => ({
				id: n.attrs.role,
				name: n.attrs.name ?? n.attrs.role ?? "...",
			}),
			(props) => {
				return <span class="mention mention-role">@{props.name}</span>;
			},
		),
		mentionEveryone: nv(
			() => ({}),
			() => {
				return <span class="mention mention-everyone">@everyone</span>;
			},
		),
		emojiCustom: nv(
			(n) => ({ id: n.attrs.id, name: n.attrs.name }),
			(props) => {
				const url = getEmojiUrl(props.id);
				return (
					<img
						class="emoji"
						src={url}
						alt={`:${props.name ?? ""}:`}
						title={`:${props.name ?? ""}:`}
					/>
				);
			},
		),
		emojiUnicode: nv(
			(n) => ({ char: n.attrs.char }),
			(props) => {
				return <UnicodeEmoji hex={getEmojiHex(props.char)} />;
			},
		),
	});
};
